
import { Component, Mixins, Prop, Emit } from "vue-property-decorator";
import { Getter } from "vuex-class";
import PaginatedSearch from "@/mixins/PaginatedSearch";
import GenericTable, { TableHeader } from "./table/generic-table.vue";
import { PaginatedSearchQueryParams, UserPermissions } from "@/store/types";
import badgeActivityApi from "@/services/api.badgeActivity.service";
import api from "@/services/api.service";
import { BadgeActivityFilterParameters, BadgeActivityPaginatedRecord, BadgeActivityRenderType, ResponseBadgeActivityDetails, ResponseBadgeActivityRequestParameters } from "@/store/badge-activity/types";
import DatePicker from "./form/DatePicker.vue";
import { Datetime } from "vue-datetime";
import { debounce } from 'lodash';
import { getDebouncePeriod } from "@/utils/debounce-helper";
import { formatDateMixin, stringMixin} from "@/mixins";

const { formatDateStr } = formatDateMixin.methods;
const { isNullOrWhitespace } = stringMixin.methods;
@Component({
    components: {
        "generic-table": GenericTable,
        "date-picker": DatePicker,
        "Datetime": Datetime
    },
})
export default class BadgeActivity extends Mixins(PaginatedSearch) {
    @Getter getPermissions: UserPermissions;

    @Prop({ type: Number, required: true }) readonly responseId: number;
    @Prop({ type: String, required: false, default: BadgeActivityRenderType.Table }) readonly renderType: BadgeActivityRenderType;
    @Prop({ type: Function, required: false }) readonly expandPanelCallback?: Function;
    @Prop({ type: Boolean, default: false }) readonly showTitle: boolean;

    private responseCanUseBadgeActivity: boolean = false;
    private isLoading: boolean = false;

    private totalRecords: number = 0;

    private inputElementsLoaded: boolean = false;

    private dateValidationError: string | null = null;

    private readonly defaultPage: number = 1;
    private readonly defaultPageSize: number = 25;

    private readonly dateErrorBothRequired: string = "Both start and end dates are required."
    private readonly dateErrorInvalid: string = "At least one of the date values is invalid";
    private readonly dateErrorStartAfterEnd: string = "Start date must be before end date.";

    private badgeActivity: BadgeActivityPaginatedRecord[] = [];

    private badgeActivityFilterParameters: BadgeActivityFilterParameters = {
        startDate: "",
        endDate: "",
        searchTerm: "",
    }

    private tableRequestParams: PaginatedSearchQueryParams = {
        page: this.defaultPage,
        pageSize: this.defaultPageSize,
        sortDesc: true,
        sortBy: "",
        searchTerm: "",
    };

    private badgeActivityData: ResponseBadgeActivityDetails = {
        responseId: 0,
        title: "",
        groupId: 0,
        extraValue: "",
        deviceServiceAddress: "",
        canBadgeActivity: false,
        dll: "",
        dllDeviceType: ""
    }

    private columns: TableHeader[] = [
        {
            title: "Message Type",
            key: "messageType",
            order: 0,
            sortOrder: 0,
            sortOrderReversed: false,
            description: "Message type",
            searchable: true,
            visible: true,
            dataType: "string",
            isTermLabel: true,
            sortable: true,
			width: "8vw",
        },
        {
            title: "Message Text",
            key: "messageText",
            order: 1,
            sortOrder: 1,
            sortOrderReversed: false,
            description: "Message text",
            searchable: true,
            visible: true,
            dataType: "string",
            isTermLabel: true,
            sortable: true,
			width: "7vw",
        },
        {
            title: "DateTime (UTC)",
            key: "dateTime",
            order: 2,
            sortOrder: 2,
            sortOrderReversed: false,
            description: "DateTime of the event",
            searchable: true,
            visible: true,
            dataType: "string",
            isTermLabel: true,
            sortable: true,
            useCustomCell: true,
			width: "10vw",
        },
    ];

    public created(): void {
        // Default the start and end date to the last 7 days
        const today = new Date();
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(today.getDate() - 7);

        this.badgeActivityFilterParameters.startDate = sevenDaysAgo.toISOString();
        this.badgeActivityFilterParameters.endDate =  today.toISOString();
    }

    public async mounted(): Promise<void> {
        // Skip API call if necessary permission and ResponseID are not available
        if (!this.responseId || this.responseId <= 0) {
            this.responseCanUseBadgeActivity = false;
            return;
        }

        const badgeActivitySupported = await badgeActivityApi.badgeActivitySupported(this.responseId);
        this.badgeActivityData = badgeActivitySupported;

        if (!badgeActivitySupported || !badgeActivitySupported.canBadgeActivity) {
            this.responseCanUseBadgeActivity = false;
            return;
        }

        if (badgeActivitySupported && isNullOrWhitespace(badgeActivitySupported.deviceServiceAddress)) {
            this.badgeActivityData.deviceServiceAddress = api.getDeviceServiceAddress();
        }

        if (badgeActivitySupported && badgeActivitySupported.canBadgeActivity) {
            this.responseCanUseBadgeActivity = true;
            this.badgeActivityFilterParameters.sourceId = badgeActivitySupported.extraValue;
        }
    }

    private async updated(): Promise<void> {
        // Only call this method in the updated once
        if (!this.inputElementsLoaded && this.$refs.startDateInput && this.$refs.endDateInput) {
            this.inputElementsLoaded = true;
            await this.searchBadgeActivity();
        }
    }

    private callbackOnButtonClick(): void {
        // Callback method which will manipulate the popout if required
        if (this.expandPanelCallback && this.renderType === BadgeActivityRenderType.Button) {
            this.expandPanelCallback();
        }
    }

    private async searchBadgeActivity(paginatedSearchQueryParams: PaginatedSearchQueryParams = null): Promise<void> {
        this.isLoading = true;
        try {
            // If this method is called manually (without search-filter-update) use our persisting search parameters to create the request
            if (!paginatedSearchQueryParams) {
                paginatedSearchQueryParams = this.tableRequestParams
            }
            // If this method is called as a result of sorting the columns in the Generic Table, update our persisting search parameters to reflect these new options
            else {
                this.tableRequestParams = { ...paginatedSearchQueryParams };
            }

            const result = await badgeActivityApi.getPaginatedBadgeActivity(this.badgeActivityData, this.badgeActivityFilterParameters, paginatedSearchQueryParams);
            this.badgeActivity = result.pageRecords as BadgeActivityPaginatedRecord[];
            this.totalRecords = result.totalRecords as number;
        }
        catch (error) {
            console.error("Error fetching badge activity: ", error);
        }
        finally {
            this.isLoading = false;
        }
    }

    private async dateRangeChange(): Promise<void> {
        if (this.inputElementsLoaded && this.validateCustomParameters()){
            await this.searchBadgeActivity();
        }
    }

    private searchTermChange = debounce(async (): Promise<void> => {
        await this.searchBadgeActivity();
    }, getDebouncePeriod());

    private validateCustomParameters(): boolean {
        if (!this.badgeActivityFilterParameters.startDate || !this.badgeActivityFilterParameters.endDate) {
            this.dateValidationError = this.dateErrorBothRequired;
            return false;
        }

        var formattedStartDate = new Date(this.badgeActivityFilterParameters.startDate);
        var formattedEndDate = new Date(this.badgeActivityFilterParameters.endDate);
        if (isNaN(formattedStartDate.getTime()) || isNaN(formattedEndDate.getTime())) {
            this.dateValidationError = this.dateErrorInvalid;
            return false;
        }

        if (formattedStartDate && formattedEndDate && new Date(formattedStartDate) > new Date(formattedEndDate)) {
            this.dateValidationError = this.dateErrorStartAfterEnd;
            return false;
        }

        this.dateValidationError = "";

        return true;
    }

    private get renderTypeTable(): string {
        return BadgeActivityRenderType.Table
    }

    private get renderTypeButton(): string {
        return BadgeActivityRenderType.Button;
    }

    private get guardedBadgeActivity(): BadgeActivityPaginatedRecord[] {
        return this.badgeActivity ?? [];
    }

	@Emit("focus-changed")
	private onInputFocusChanged(focus: boolean): boolean {
		return focus;
	}
}
